import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// useConfirm — one reusable "Are you sure?" prompt for the whole app.
//
// Mount <ConfirmProvider> once near the root, then in any handler:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: "Remove portal?", destructive: true }))) return;
//
// Built on the existing shadcn AlertDialog primitive — no new dependency.
// ---------------------------------------------------------------------------

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  description: "",
  confirmText: "Confirm",
  cancelText: "Cancel",
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null); // null = closed
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpts({ ...DEFAULTS, ...options });
    });
  }, []);

  // Resolve the pending promise exactly once, then close. Both the button
  // clicks and Escape/dismiss route through here; the ref-clear makes the
  // first answer win (an Action click closes the dialog, which also fires
  // onOpenChange(false) — that second call is a no-op).
  const settle = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setOpts(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={opts !== null} onOpenChange={(open) => !open && settle(false)}>
        {opts && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{opts.title}</AlertDialogTitle>
              {opts.description && (
                <AlertDialogDescription>{opts.description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => settle(false)}>
                {opts.cancelText}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => settle(true)}
                className={opts.destructive ? buttonVariants({ variant: "destructive" }) : undefined}
              >
                {opts.confirmText}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

// Fallback for when no <ConfirmProvider> is mounted (unit tests, or a
// forgotten provider): degrade to native confirm so the gate still works.
// ponytail: native fallback, not a throw — a missing provider should still
// confirm, not white-screen the app.
function nativeConfirm({ title, description } = {}) {
  const msg = [title, description].filter(Boolean).join("\n\n");
  return Promise.resolve(window.confirm(msg));
}

export function useConfirm() {
  return useContext(ConfirmContext) ?? nativeConfirm;
}
